import { type RefObject } from 'react';
import { type HostInstance, Platform } from 'react-native';

import { fromPromise } from 'xstate';

import { type Context, type TargetMeasurement } from '../types';

// ─────────────────────────────────────────────────────────────────────────────

export const measureAllTargets = fromPromise<
  Record<string, TargetMeasurement>,
  { context: Context }
>(async ({ input }) => {
  const { scrollViewRef, scrollOffset, targetRefs } = input.context;
  const scrollViewNode = (scrollViewRef.current as HostInstance | null) ?? null;

  if (!scrollViewNode) return {};

  await waitForNextFrame();

  const entries = Object.entries(targetRefs);
  const measureLayoutPromises = entries.map(([targetId, targetRef]) =>
    measureLayout(targetId, targetRef, scrollViewNode, scrollOffset.value),
  );

  const results = await Promise.all(measureLayoutPromises);

  return results.reduce<Record<string, TargetMeasurement>>((measurements, result) => {
    if (!result) return measurements;

    return { ...measurements, [result.targetId]: result.measurement };
  }, {});
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

function measureLayout(
  targetId: string,
  targetRef: RefObject<unknown>,
  scrollViewNode: HostInstance,
  scrollOffsetValue: number,
): Promise<{ targetId: string; measurement: TargetMeasurement } | null> {
  return new Promise((resolve) => {
    const targetNode = (targetRef.current as HostInstance | null) ?? null;

    if (!targetNode) {
      resolve(null);

      return;
    }

    targetNode.measureLayout(
      scrollViewNode,
      (_x: number, y: number, _width: number, height: number) => {
        const contentY = Platform.select({ web: y + scrollOffsetValue, default: y });

        resolve({ targetId, measurement: { y: contentY, height } });
      },
      () => {
        resolve(null);
      },
    );
  });
}

// ─────────────────────────────────────────────────────────────────────────────

export const Actors = {
  measureAllTargets,
};
